/**
 * Calendar Utility Functions
 *
 * Date math, grid builders, and overlap layout. ALL recurrence reasoning
 * lives in `@fias/calendar-recurrence` — the platform's single, shared,
 * library-backed implementation (this file used to carry three hand-rolled
 * RRULE readers of its own; each got a different RFC 5545 edge wrong). The
 * thin wrappers here only resolve the evaluation ZONE the way the client
 * means it and re-export the shared functions under their historical names.
 */

import {
  canonicalRrule,
  ruleForNewStart as sharedRuleForNewStart,
  type RuleAdjustment as SharedRuleAdjustment,
} from '@fias/calendar-recurrence';
import type {
  EventInstance,
  CalendarView,
  CalendarCollection,
  CalendarSettings,
  QuickEventDraft,
  TemperatureUnit,
  RecurrenceEditScope,
} from './types';
import { DAY_NAMES, MONTH_NAMES, HOUR_HEIGHT_PX } from './types';

// The dialog model (parse/build/label) moved to the shared package with the
// rest of the recurrence machinery; re-exported here so the arche's many
// import sites keep working unchanged.
export {
  parseRruleToRule,
  buildRruleString,
  buildRecurrenceLabel,
  seriesEndMs,
} from '@fias/calendar-recurrence';

// =============================================================================
// DATE MATH
// =============================================================================

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfWeek(date: Date, weekStartDay: number = 0): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day - weekStartDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfWeek(date: Date, weekStartDay: number = 0): Date {
  const d = startOfWeek(date, weekStartDay);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// =============================================================================
// VISIBLE RANGE COMPUTATION
// =============================================================================

export function getVisibleRange(
  currentDate: Date,
  view: CalendarView,
  settings: CalendarSettings,
): { start: Date; end: Date } {
  switch (view) {
    case 'month': {
      // Month grid starts at the beginning of the first visible week
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const gridStart = startOfWeek(monthStart, settings.weekStartDay);
      const gridEnd = endOfWeek(monthEnd, settings.weekStartDay);
      return { start: gridStart, end: gridEnd };
    }
    case 'week': {
      const weekStart = startOfWeek(currentDate, settings.weekStartDay);
      const weekEnd = endOfWeek(currentDate, settings.weekStartDay);
      return { start: weekStart, end: weekEnd };
    }
    case 'day': {
      return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    }
    case 'agenda': {
      const agendaStart = startOfDay(currentDate);
      const agendaEnd = addDays(agendaStart, settings.agendaDaysAhead);
      return { start: agendaStart, end: agendaEnd };
    }
  }
}

// =============================================================================
// GRID BUILDERS
// =============================================================================

export interface MonthGridCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventInstance[];
}

/**
 * Does `event` occupy the calendar day `dayDate` represents?
 *
 * All-day events are stored as UTC-midnight instants with an EXCLUSIVE end —
 * an all-day event on Aug 13 is `2026-08-13T00:00Z → 2026-08-14T00:00Z`,
 * mirroring the RFC 5545 DATE form Google and CalDAV emit. They are floating
 * DATES, not instants, so they must never be converted through the viewer's
 * timezone: west of UTC, `00:00Z` renders as 18:00 the previous evening, and
 * a one-day event then overlaps two local days and is drawn twice (observed in
 * QA in America/Denver — one chip on Aug 12, one on Aug 13). Compare calendar
 * dates in UTC instead.
 *
 * Timed events ARE real instants and belong in local time, so they keep the
 * local-boundary overlap test — narrowed by the one-night rule below, which
 * keeps an event that merely runs past midnight from being LISTED twice.
 */
/**
 * All-day events are DATES, not instants, and the platform stores them the way
 * every provider does: UTC midnight for the start, and an EXCLUSIVE UTC
 * midnight for the end (a one-day event on the 26th ends on the 27th).
 * `eventOccupiesDay` below reads them back with UTC date components.
 *
 * The form must use the same convention, which is why these three helpers live
 * next to that reader rather than inside the dialog. Encoding an all-day range
 * from LOCAL midnight instead put every user east of UTC one day early — the
 * stored instant fell on the previous UTC date, so the chip rendered on the
 * wrong day AND the wrong date was pushed to Outlook and iCloud. It looked
 * correct in the Americas, which is the only reason it survived QA.
 */
const ALL_DAY_MS = 86_400_000;

/**
 * Timed events carry their own IANA timezone (`vault_calendar_events.timezone`,
 * and Graph sends one on every event). The form used to interpret its date and
 * time inputs in whatever zone the BROWSER happened to be in, so an event
 * created for a Tokyo meeting while travelling was stored at the traveller's
 * wall-clock time, and reopening an event abroad silently rewrote it on save.
 *
 * These convert between the paired date/time inputs and the stored instant in
 * an explicit zone. `Intl` is the only source of truth for offsets — hard-coded
 * tables go stale, and DST rules change by legislation.
 */
/**
 * A zone `Intl` will actually accept, falling back to the viewer's.
 *
 * An event can arrive carrying a zone this browser does not know — most
 * plausibly a Windows id that the Windows->IANA mapping did not cover, which is
 * exactly the class of data this sync work introduces. `Intl.DateTimeFormat`
 * throws `RangeError` on one, and the conversions below run inside the event
 * dialog's open effect, so the throw unwound to the nearest error boundary
 * instead of producing anything the user could act on: the dialog simply would
 * not open.
 *
 * Falling back is right here because this is a DISPLAY concern — the stored
 * instant is unaffected, and showing the event in the viewer's zone beats
 * showing nothing. The push path takes the opposite line and refuses, because
 * there a wrong zone silently reschedules the event on the real calendar.
 */
export function usableTimeZone(timeZone: string | null | undefined): string {
  if (!timeZone) return browserTimeZone();
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
    return timeZone;
  } catch {
    return browserTimeZone();
  }
}

function timeZoneOffsetMs(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const at = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // `hour` comes back as 24 at midnight under hour12:false in some engines.
  const asIfUtc = Date.UTC(
    at('year'),
    at('month') - 1,
    at('day'),
    at('hour') % 24,
    at('minute'),
    at('second'),
  );
  return asIfUtc - instant;
}

/** The wall-clock the user typed, read in `timeZone`, as a stored instant. */
export function zonedInputsToUtcIso(date: string, time: string, timeZone: string): string {
  const zone = usableTimeZone(timeZone);
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = (time || '00:00').split(':').map(Number);
  const naive = Date.UTC(y, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  // Two passes: the first offset is sampled at the wrong instant when the guess
  // lands on the other side of a DST transition, the second corrects it.
  let instant = naive - timeZoneOffsetMs(naive, zone);
  instant = naive - timeZoneOffsetMs(instant, zone);

  // A wall time inside a spring-forward GAP does not exist, and the two passes
  // resolve it in whichever direction the zone's transition happens to fall —
  // America/New_York landed an hour EARLIER than the user typed while
  // Europe/Berlin landed an hour later. Detect it by reading the instant back:
  // if it does not render as the requested wall time, the time was skipped, so
  // shift FORWARD past the gap. That matches what calendars do with a
  // nonexistent time and is at least consistent across zones.
  // A wall time inside a spring-forward GAP does not exist, and the two passes
  // then resolve it in whichever direction the zone's transition happens to
  // fall — America/New_York landed an hour EARLIER than the user typed while
  // Europe/Berlin landed an hour later. Detect it by reading the instant back:
  // if it does not render as the requested wall time, the time was skipped.
  //
  // Resolve a skipped time with the offset in force BEFORE the transition,
  // which lands just past the gap in every zone (02:30 -> 03:30). That is what
  // calendars do with a nonexistent time, and it is at least the same answer
  // everywhere rather than zone-dependent.
  if (instant + timeZoneOffsetMs(instant, zone) !== naive) {
    instant = naive - timeZoneOffsetMs(naive - 24 * 60 * 60 * 1000, zone);
  }

  return new Date(instant).toISOString();
}

/** A stored instant as the wall-clock date and time inputs for `timeZone`. */
export function utcIsoToZonedInputs(iso: string, timeZone: string): { date: string; time: string } {
  const zone = usableTimeZone(timeZone);
  const instant = new Date(iso).getTime();
  // `Intl.formatToParts` throws RangeError on an invalid Date, and this runs
  // inside the dialog's save path — an unparseable instant took the whole save
  // down with "Invalid time value" rather than doing anything useful.
  if (!Number.isFinite(instant)) return { date: '', time: '' };
  const shifted = new Date(instant + timeZoneOffsetMs(instant, zone));
  return {
    date: formatUtcDateForInput(shifted),
    time: `${String(shifted.getUTCHours()).padStart(2, '0')}:${String(
      shifted.getUTCMinutes(),
    ).padStart(2, '0')}`,
  };
}

/** The viewer's own zone — the default for a new event, as everywhere else. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Zones offered before the user types anything.
 *
 * The full IANA database is ~450 zones, most of them aliases or places nobody
 * schedules against ("America/Indiana/Petersburg"), and a flat list of them is
 * unusable — a US user wants four entries, not four hundred. This is the
 * shortlist; SEARCH still runs over the complete `supportedTimeZones()` list,
 * so nothing is unreachable and an event already in an obscure zone can always
 * keep it. Curating the default view is a UI decision; removing zones would be
 * a correctness one.
 */
export const COMMON_TIME_ZONES: ReadonlyArray<{ zone: string; label: string }> = [
  { zone: 'America/Los_Angeles', label: 'Pacific Time' },
  { zone: 'America/Denver', label: 'Mountain Time' },
  { zone: 'America/Phoenix', label: 'Arizona' },
  { zone: 'America/Chicago', label: 'Central Time' },
  { zone: 'America/New_York', label: 'Eastern Time' },
  { zone: 'America/Anchorage', label: 'Alaska Time' },
  { zone: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { zone: 'America/Toronto', label: 'Toronto' },
  { zone: 'America/Vancouver', label: 'Vancouver' },
  { zone: 'America/Mexico_City', label: 'Mexico City' },
  { zone: 'America/Bogota', label: 'Bogotá' },
  { zone: 'America/Sao_Paulo', label: 'São Paulo' },
  { zone: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires' },
  { zone: 'UTC', label: 'Coordinated Universal Time' },
  { zone: 'Europe/London', label: 'London' },
  { zone: 'Europe/Dublin', label: 'Dublin' },
  { zone: 'Europe/Lisbon', label: 'Lisbon' },
  { zone: 'Europe/Madrid', label: 'Madrid' },
  { zone: 'Europe/Paris', label: 'Paris' },
  { zone: 'Europe/Berlin', label: 'Berlin' },
  { zone: 'Europe/Rome', label: 'Rome' },
  { zone: 'Europe/Amsterdam', label: 'Amsterdam' },
  { zone: 'Europe/Stockholm', label: 'Stockholm' },
  { zone: 'Europe/Warsaw', label: 'Warsaw' },
  { zone: 'Europe/Athens', label: 'Athens' },
  { zone: 'Europe/Istanbul', label: 'Istanbul' },
  { zone: 'Europe/Moscow', label: 'Moscow' },
  { zone: 'Africa/Lagos', label: 'Lagos' },
  { zone: 'Africa/Cairo', label: 'Cairo' },
  { zone: 'Africa/Nairobi', label: 'Nairobi' },
  { zone: 'Africa/Johannesburg', label: 'Johannesburg' },
  { zone: 'Asia/Dubai', label: 'Dubai' },
  { zone: 'Asia/Karachi', label: 'Karachi' },
  { zone: 'Asia/Kolkata', label: 'India' },
  { zone: 'Asia/Dhaka', label: 'Dhaka' },
  { zone: 'Asia/Bangkok', label: 'Bangkok' },
  { zone: 'Asia/Jakarta', label: 'Jakarta' },
  { zone: 'Asia/Singapore', label: 'Singapore' },
  { zone: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { zone: 'Asia/Shanghai', label: 'China' },
  { zone: 'Asia/Seoul', label: 'Seoul' },
  { zone: 'Asia/Tokyo', label: 'Japan' },
  { zone: 'Australia/Perth', label: 'Perth' },
  { zone: 'Australia/Brisbane', label: 'Brisbane' },
  { zone: 'Australia/Sydney', label: 'Sydney' },
  { zone: 'Pacific/Auckland', label: 'Auckland' },
];

const COMMON_LABEL_BY_ZONE = new Map(COMMON_TIME_ZONES.map((t) => [t.zone, t.label]));

/** "Pacific Time" for a curated zone, otherwise the city out of the IANA id. */
export function timeZoneLabel(zone: string): string {
  const curated = COMMON_LABEL_BY_ZONE.get(zone);
  if (curated) return curated;
  const city = zone.split('/').pop() ?? zone;
  return city.replace(/_/g, ' ');
}

/**
 * Current UTC offset of a zone, e.g. "GMT-6" — the thing people actually
 * recognise a zone by, and the only way to tell two same-named cities apart.
 * Offsets shift with DST, so this is computed for `at`, never cached.
 */
export function timeZoneOffsetLabel(zone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Every zone the browser knows. Used for SEARCH, so a zone outside the curated
 * shortlist is still reachable by typing. Where `supportedValuesOf` is missing,
 * fall back to the shortlist rather than an empty result.
 */
export function supportedTimeZones(): string[] {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    if (supported?.length) return supported;
  } catch {
    /* fall through */
  }
  return COMMON_TIME_ZONES.map((t) => t.zone);
}

/**
 * Fields a change to which cannot be stored on a SINGLE occurrence.
 *
 * `vault_calendar_event_exceptions` carries title, description, location,
 * start/end, all-day, status, colour and reminders — and nothing else. An edit
 * scoped to one occurrence therefore cannot express a new URL, tags, a move to
 * another calendar, a timezone, or a recurrence change; those belong to the
 * series row. The mutation used to accept them and drop them on the floor,
 * reporting success.
 */
const SERIES_ONLY_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'url', label: 'the link' },
  { key: 'tags', label: 'tags' },
  { key: 'calendarId', label: 'the calendar' },
  { key: 'timezone', label: 'the timezone' },
  { key: 'rrule', label: 'the repeat rule' },
];

/** Which series-only fields this edit would change, by user-facing label.
 *  Rule comparison uses the SHARED `canonicalRrule` (the same folding the
 *  sync engine's diff uses — this file used to carry a hand-rolled mirror
 *  of it that folded differently). */

/**
 * Empty is empty. The event row carries `''` for an unset link while the form
 * sends `undefined`, and treating those as different reported "the link
 * changed" to a user who had only nudged an occurrence by five minutes.
 */
function emptyToNull(value: unknown): unknown {
  return value === '' || value === undefined ? null : value;
}

export function seriesOnlyChanges(
  original: Record<string, unknown>,
  next: Record<string, unknown>,
): string[] {
  return SERIES_ONLY_FIELDS.filter(({ key }) => {
    // `undefined` in a PATCH means "leave this alone", NOT "clear it" — which
    // is exactly how `updateVaultCalendarEvent` reads it. Treating an omitted
    // field as a change refused a five-minute nudge with "Changing the
    // timezone applies to the whole series", because the dialog omits
    // `timezone` while the column is NOT NULL DEFAULT 'UTC' and so never
    // matches null. Same shape for all-day events, which always omit it.
    if (!(key in next) || next[key] === undefined) return false;
    let before = emptyToNull(original[key]);
    let after = emptyToNull(next[key]);
    // Tags compare as sets — order is not a change.
    if (Array.isArray(before) || Array.isArray(after)) {
      const norm = (v: unknown) => JSON.stringify([...((v as string[]) ?? [])].sort());
      return norm(before) !== norm(after);
    }
    if (key === 'rrule') {
      before = canonicalRrule(before);
      after = canonicalRrule(after);
    }
    return before !== after;
  }).map((f) => f.label);
}

/** "a", "a and b", "a, b and c" — for a sentence, not a list. */
export function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// =============================================================================
// WHERE A NEW EVENT LANDS
// =============================================================================

/** The slice of a calendar the placement rule reads. */
export type NewEventCalendarCandidate = Pick<CalendarCollection, 'id' | 'isDefault' | 'isVisible'>;

export interface ResolveNewEventCalendarOptions {
  /** A calendar the draft already names (a template, the quick box, a parse). */
  draftCalendarId?: string | null;
  /** The user's "default calendar" preference (`settings.defaultCalendarId`). */
  preferredCalendarId?: string | null;
}

/**
 * Picks the calendar a NEW event or task starts on — the ONE rule for the
 * event dialog, the quick-create box, drag-a-task-to-a-time and the task
 * dialog (each used to carry its own variant, and none of them looked at
 * visibility, so a new event could land on a calendar the user had hidden in
 * the left panel and simply never appear).
 *
 * In order: a calendar the draft names if it is enabled → the user's default
 * calendar if it is enabled → the server-flagged default calendar if it is
 * enabled → the first enabled calendar → the first calendar at all (a user
 * who hid everything still gets a working form) → `null` when there are no
 * calendars. A hidden calendar never wins while an enabled one exists, even
 * when a template names it: the event would be saved and not be seen.
 */
export function resolveNewEventCalendarId(
  calendars: ReadonlyArray<NewEventCalendarCandidate>,
  { draftCalendarId, preferredCalendarId }: ResolveNewEventCalendarOptions = {},
): string | null {
  const byId = (id: string | null | undefined) =>
    id ? calendars.find((c) => c.id === id) : undefined;
  const draft = byId(draftCalendarId);
  if (draft?.isVisible) return draft.id;
  const preferred = byId(preferredCalendarId);
  if (preferred?.isVisible) return preferred.id;
  const flaggedDefault = calendars.find((c) => c.isDefault && c.isVisible);
  if (flaggedDefault) return flaggedDefault.id;
  const firstVisible = calendars.find((c) => c.isVisible);
  if (firstVisible) return firstVisible.id;
  return calendars[0]?.id ?? null;
}

export const RRULE_WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

/**
 * What moving a series start means for its recurrence rule — re-exported
 * from the shared package (`unchanged` / `rewritten` / `unsupported`;
 * callers must handle `unsupported` by refusing the save, because for
 * Microsoft a contradicting start-only update is the silent-discard path:
 * 200 OK, nothing moves, reverted on the next pull).
 */
export type RuleAdjustment = SharedRuleAdjustment;

/**
 * The zone a series' rule should be read in.
 *
 * An all-day event is a DATE stored at UTC midnight, and the platform moves
 * it in whole UTC days — so its weekday and day-of-month are UTC facts.
 * Reading one in the viewer's zone puts every user WEST of UTC a day early:
 * `2026-08-20T00:00:00Z` is the 19th at 18:00 in Denver.
 *
 * A TIMED series reads in its STORED anchor (`rruleTimezone ?? timezone`,
 * which callers pass as `timeZone`) — the same zone the server's
 * in-transaction reconciliation resolves, so the two verdicts agree by
 * construction. The browser zone is only the last-resort fallback for a
 * caller with no event at hand.
 */
function ruleZoneFor(isAllDay: boolean | undefined, timeZone: string | null | undefined): string {
  return isAllDay ? 'UTC' : usableTimeZone(timeZone ?? browserTimeZone());
}

/**
 * A recurrence rule reconciled with a new series start — the thin client
 * adapter over the shared implementation ("satisfied, or refused — never
 * reshaped"; the full contract, the measured Graph silent-discard evidence,
 * and the reverted-rewrite graveyard live with the shared code). This
 * wrapper exists only to resolve the zone the client way and to keep the
 * historical option names at the two call sites.
 */
export function ruleForNewStart(
  rrule: string | null,
  startIso: string,
  options: {
    /** The series' STORED anchor zone (`rruleTimezone ?? timezone`). */
    timeZone?: string | null;
    /** Start this edit began from. ABSENT MEANS THERE IS NO MOVE — a
     *  create, or a template; nothing is adjusted and nothing is refused. */
    previousStartIso?: string | null;
    isAllDay?: boolean;
    /** The previous start is read in ITS OWN representation — pass the
     *  pre-edit all-day flag when the edit toggles all-day. */
    previousIsAllDay?: boolean;
  } = {},
): RuleAdjustment {
  return sharedRuleForNewStart(rrule, startIso, {
    zone: ruleZoneFor(options.isAllDay, options.timeZone),
    previousStartIso: options.previousStartIso,
    isAllDay: options.isAllDay,
    previousIsAllDay: options.previousIsAllDay,
  });
}

/** UTC date part of an instant, formatted for `<input type="date">`. */
export function formatUtcDateForInput(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;
}

/**
 * Encode an all-day range picked in the form as stored instants: UTC midnight
 * start, exclusive UTC midnight end.
 */
export function encodeAllDayRange(
  startDate: string,
  endDate: string,
): { startAt: string; endAt: string } {
  const startAt = new Date(`${startDate}T00:00:00.000Z`);
  const endExclusive = new Date(new Date(`${endDate}T00:00:00.000Z`).getTime() + ALL_DAY_MS);
  return { startAt: startAt.toISOString(), endAt: endExclusive.toISOString() };
}

/**
 * The INCLUSIVE last date an all-day event occupies — what the form's "End
 * date" field should show.
 *
 * Mirrors `eventOccupiesDay`'s span exactly, including its floor, so the date
 * the user sees is the last day the grid actually draws a chip on. That also
 * makes it correct for rows written under older conventions (a local-midnight
 * `23:59:59.999` end) without needing to know which convention wrote them.
 */
export function allDayLastDate(startAt: string, endAt: string): Date {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endUtc = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  return new Date(Math.max(endUtc, startUtc + ALL_DAY_MS) - ALL_DAY_MS);
}

/**
 * Does this timed event cross midnight as ONE evening rather than as a
 * genuinely multi-day booking?
 *
 * "Tuesday 11:30pm–12:30am" is a Tuesday meeting. Listing it on Wednesday too
 * is not a second view of one booking — in a month cell and in the agenda it
 * renders as a second CHIP carrying the 11:30 PM start, so a weekly series
 * reads as two weekly series on two different weekdays. That is what it looked
 * like against Google, which lists the meeting on Tuesday only.
 *
 * The line is the one the event dialog already draws (`handleAllDayChange`'s
 * `spansOneNight`): crossing midnight within 24h is one evening; anything
 * longer is a real multi-day range and still occupies every day it covers.
 * Same rule, read side — the two must not drift.
 */
function spansOneNight(start: Date, end: Date): boolean {
  return end.getTime() - start.getTime() <= ALL_DAY_MS;
}

/** The later-day tail of a one-night event — drawn in the time grid, but never
 *  listed as its own entry. */
function isOvernightSpill(start: Date, end: Date, dayDate: Date): boolean {
  return startOfDay(dayDate).getTime() > startOfDay(start).getTime() && spansOneNight(start, end);
}

/**
 * Raw overlap: does any part of this event fall inside `dayDate`?
 *
 * `eventOccupiesDay` deliberately narrows this for LISTING surfaces (a
 * one-night event is anchored to its start day so it is not listed twice). The
 * agenda needs both: multi-day bars use the narrowed rule, everything else uses
 * this one, because anchoring dropped an overnight event that started the day
 * BEFORE the agenda's first day out of the list entirely.
 */
export function overlapsDay(event: EventInstance, dayDate: Date): boolean {
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);
  if (event.isAllDay) return eventOccupiesDay(event, dayDate);
  return start < endOfDay(dayDate) && end > startOfDay(dayDate);
}

export function eventOccupiesDay(event: EventInstance, dayDate: Date): boolean {
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);

  if (!event.isAllDay) {
    if (!(start < endOfDay(dayDate) && end > startOfDay(dayDate))) return false;
    return !isOvernightSpill(start, end, dayDate);
  }

  // The cell's calendar date, as a UTC instant — compared against the event's
  // UTC date components so neither side passes through a timezone offset.
  const dayUtc = Date.UTC(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
  const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endUtc = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());

  // End is exclusive. Some providers emit end == start for a single all-day
  // event rather than the following midnight; floor the span at one day so
  // such an event still renders instead of vanishing.
  return dayUtc >= startUtc && dayUtc < Math.max(endUtc, startUtc + ALL_DAY_MS);
}

/**
 * Is this event drawn as a multi-day BAR rather than as a chip in a day?
 *
 * Google, Apple and Outlook all render a genuinely multi-day booking as one
 * continuous bar — in the month grid, and in the week/day views as a bar in the
 * all-day band above the time grid rather than as a block inside it. Confirmed
 * against Google for a Mon 9am -> Wed 5pm event: a single bar in month, week
 * and day, carrying just the title.
 *
 * The boundary is the same 24h line `spansOneNight` already draws, which is why
 * it is reused rather than a second threshold invented next to it:
 *
 *   - <=24h  — one sitting, even if it crosses midnight. Stays in the time
 *     grid, anchored to the day it starts on (a Tue 23:30-00:30 meeting is a
 *     Tuesday meeting). Verified against Google, which shows exactly that.
 *   - >24h   — a real multi-day span. Becomes a bar.
 *
 * All-day events are always bars: that is what the all-day band is.
 */
export function isMultiDayBarEvent(event: EventInstance): boolean {
  if (event.isAllDay) return true;
  return !spansOneNight(new Date(event.startAt), new Date(event.endAt));
}

/** One multi-day bar, placed across a row of consecutive day columns. */
export interface SpanningBar {
  event: EventInstance;
  /** 0-based index of the first column the bar covers. */
  startIndex: number;
  /** How many columns it covers (>= 1). */
  span: number;
  /** The event really begins before this row of days. */
  continuesBefore: boolean;
  /** ...and/or continues past the end of it. */
  continuesAfter: boolean;
  /** Stacking row, so overlapping bars never collide. */
  lane: number;
}

/**
 * Lay multi-day events out as bars across `days` (one month week row, a week
 * view's columns, or a single day).
 *
 * Coverage is decided by `eventOccupiesDay`, so all-day events keep their UTC
 * DATE comparison and timed ones keep local boundaries — the two conventions
 * that must not be mixed. Bars are packed into lanes greedily, longest first,
 * which is what keeps a long bar on a stable row instead of jumping between
 * lanes as short ones come and go.
 */
/**
 * Does the event really begin before this row of days — and end after it?
 *
 * All-day entries must be compared as UTC DATES and timed ones as instants, the
 * same split `eventOccupiesDay` makes. Comparing an all-day event's UTC-midnight
 * `startAt` against a LOCAL `startOfDay` is wrong everywhere west of UTC: in
 * America/Denver `2026-09-06T00:00Z` reads as Sep 5 18:00 local, so an all-day
 * event starting on the row's FIRST day was flagged as continuing in from the
 * previous week and drew a `◀` and a squared-off left edge. East of UTC the
 * mirror image happens to `continuesAfter`.
 */
function startsBeforeRow(event: EventInstance, firstDay: Date): boolean {
  const start = new Date(event.startAt);
  if (!event.isAllDay) return start < startOfDay(firstDay);
  const rowUtc = Date.UTC(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
  const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  return startUtc < rowUtc;
}

function endsAfterRow(event: EventInstance, lastDay: Date): boolean {
  const end = new Date(event.endAt);
  if (!event.isAllDay) return end > endOfDay(lastDay);
  const start = new Date(event.startAt);
  const rowUtc = Date.UTC(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());
  const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  // Exclusive end, floored at one day — the same shape eventOccupiesDay uses.
  const endUtc = Math.max(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()),
    startUtc + ALL_DAY_MS,
  );
  return endUtc > rowUtc + ALL_DAY_MS;
}

export function layoutSpanningBars(events: EventInstance[], days: Date[]): SpanningBar[] {
  if (days.length === 0) return [];
  const placed: Omit<SpanningBar, 'lane'>[] = [];
  for (const event of events) {
    if (!isMultiDayBarEvent(event)) continue;
    let first = -1;
    let last = -1;
    for (let i = 0; i < days.length; i += 1) {
      if (!eventOccupiesDay(event, days[i])) continue;
      if (first === -1) first = i;
      last = i;
    }
    if (first === -1) continue;
    placed.push({
      event,
      startIndex: first,
      span: last - first + 1,
      continuesBefore: startsBeforeRow(event, days[0]),
      continuesAfter: endsAfterRow(event, days[days.length - 1]),
    });
  }

  // Longest first at a given start, so the widest bars take the top lanes and
  // stay put; title last purely to make the order deterministic in tests.
  placed.sort(
    (a, b) =>
      a.startIndex - b.startIndex ||
      b.span - a.span ||
      (a.event.title ?? '').localeCompare(b.event.title ?? ''),
  );

  const laneEnds: number[] = [];
  return placed.map((bar) => {
    let lane = laneEnds.findIndex((end) => end <= bar.startIndex);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = bar.startIndex + bar.span;
    return { ...bar, lane };
  });
}

export function buildMonthGrid(
  currentDate: Date,
  events: EventInstance[],
  weekStartDay: number = 0,
): MonthGridCell[][] {
  const monthStart = startOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart, weekStartDay);

  const weeks: MonthGridCell[][] = [];
  let current = new Date(gridStart);

  for (let w = 0; w < 6; w++) {
    const week: MonthGridCell[] = [];
    for (let d = 0; d < 7; d++) {
      const cellDate = new Date(current);
      // Bar events are drawn ONCE by the spanning-bar layer, so they must not
      // also appear as a per-day chip — that double-render is the "one meeting,
      // three 9:00 AM chips" bug.
      const dayEvents = events.filter(
        (e) => !isMultiDayBarEvent(e) && eventOccupiesDay(e, cellDate),
      );

      week.push({
        date: cellDate,
        isCurrentMonth: isSameMonth(cellDate, currentDate),
        isToday: isToday(cellDate),
        events: dayEvents,
      });

      current = addDays(current, 1);
    }
    weeks.push(week);
  }

  return weeks;
}

export function getWeekDays(currentDate: Date, weekStartDay: number = 0): Date[] {
  const weekStart = startOfWeek(currentDate, weekStartDay);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(weekStart, i));
  }
  return days;
}

// =============================================================================
// TIME GRID EVENT POSITIONING
// =============================================================================

export interface PositionedEvent {
  event: EventInstance;
  top: number;
  height: number;
  left: number;
  width: number;
  column: number;
  totalColumns: number;
  /**
   * The portion of the event that falls inside THIS column's day, clamped to
   * the day's midnight boundaries. For an event contained in one day these are
   * the event's own start/end.
   *
   * Load-bearing for the label: the block is already positioned by the clamped
   * segment, but it used to be labelled with the event's own times, so the
   * 12:00–12:30am tail of a Tue 11:30pm meeting was drawn at midnight on
   * Wednesday reading "11:30 PM – 12:30 AM" — a second 11:30 PM meeting, as
   * far as the grid showed.
   */
  segmentStart: Date;
  segmentEnd: Date;
  /** True on the later-day tail of an event that started on a previous day. */
  isContinuation: boolean;
}

export function positionTimedEvents(
  events: EventInstance[],
  dayDate: Date,
  hourHeightPx: number = HOUR_HEIGHT_PX,
): PositionedEvent[] {
  // Filter to timed events (not all-day) that overlap this day
  const timedEvents = events.filter((e) => {
    // All-day AND multi-day: both are drawn as bars in the band above, so the
    // hour grid must not draw them again.
    if (isMultiDayBarEvent(e)) return false;
    const start = new Date(e.startAt);
    const end = new Date(e.endAt);
    return start < endOfDay(dayDate) && end > startOfDay(dayDate);
  });

  if (timedEvents.length === 0) return [];

  // Calculate pixel positions
  const positioned = timedEvents.map((event) => {
    const start = new Date(event.startAt);
    const end = new Date(event.endAt);

    // Clamp to day boundaries. The end clamps to the NEXT midnight rather than
    // to 23:59:59.999 so a segment running to the end of the day is labelled
    // "12:00 AM" and measures its true length, instead of losing a minute.
    const dayStart = startOfDay(dayDate);
    const nextDayStart = addDays(dayStart, 1);
    const segmentStart = start < dayStart ? dayStart : start;
    const segmentEnd = end > nextDayStart ? nextDayStart : end;

    // Offsets are WALL-CLOCK minutes, not elapsed time: the grid is a fixed
    // 24 rows, and a DST day is 23 or 25 real hours, so measuring elapsed ms
    // from midnight would shift every block on the two transition days.
    // A segment reaching the day's end reads 24:00 rather than 00:00 of the
    // next day, which is the only case getHours() cannot express.
    const startMinutes = segmentStart.getHours() * 60 + segmentStart.getMinutes();
    const endMinutes =
      segmentEnd.getTime() >= nextDayStart.getTime()
        ? 24 * 60
        : segmentEnd.getHours() * 60 + segmentEnd.getMinutes();
    const durationMinutes = Math.max(endMinutes - startMinutes, 15); // Min 15 min height

    const top = (startMinutes / 60) * hourHeightPx;
    const height = (durationMinutes / 60) * hourHeightPx;

    return {
      event,
      top,
      height,
      left: 0,
      width: 100,
      column: 0,
      totalColumns: 1,
      startMinutes,
      endMinutes: startMinutes + durationMinutes,
      segmentStart,
      segmentEnd,
      isContinuation: start < dayStart,
    };
  });

  // Sort by start time, then by duration (longer events first)
  positioned.sort((a, b) => a.startMinutes - b.startMinutes || b.height - a.height);

  // Resolve overlaps using column layout
  const columns: { end: number }[] = [];

  for (const item of positioned) {
    // Find first available column
    let col = 0;
    for (col = 0; col < columns.length; col++) {
      if (columns[col].end <= item.startMinutes) break;
    }

    if (col === columns.length) {
      columns.push({ end: item.endMinutes });
    } else {
      columns[col].end = item.endMinutes;
    }

    item.column = col;
  }

  const totalColumns = columns.length;

  return positioned.map((item) => ({
    event: item.event,
    top: item.top,
    height: item.height,
    left: (item.column / totalColumns) * 100,
    width: 100 / totalColumns,
    column: item.column,
    totalColumns,
    segmentStart: item.segmentStart,
    segmentEnd: item.segmentEnd,
    isContinuation: item.isContinuation,
  }));
}

/**
 * What the all-day BAND above the time grid shows on this day.
 *
 * Not just `isAllDay` any more: a timed booking longer than 24h belongs in the
 * band too, as one bar, which is where Google/Apple/Outlook put it. Leaving it
 * in the hour grid drew it as three separate blocks — one per day it touched,
 * each labelled with the original 9:00 AM start.
 */
export function getAllDayEvents(events: EventInstance[], dayDate: Date): EventInstance[] {
  return events.filter((e) => isMultiDayBarEvent(e) && eventOccupiesDay(e, dayDate));
}

// =============================================================================
// FORMATTING
// =============================================================================

export function formatDateLabel(date: Date, view: CalendarView): string {
  switch (view) {
    case 'month':
      return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
    case 'week': {
      const weekEnd = addDays(date, 6);
      if (date.getMonth() === weekEnd.getMonth()) {
        return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()} – ${weekEnd.getDate()}, ${date.getFullYear()}`;
      }
      return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${date.getFullYear()}`;
    }
    case 'day':
      return `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    case 'agenda':
      return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  }
}

function formatClock(hours: number, minutes: number, timeFormat: '12h' | '24h'): string {
  if (timeFormat === '24h') {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  const displayMinutes = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
  return `${displayHour}${displayMinutes} ${ampm}`;
}

export function formatTime(date: Date, timeFormat: '12h' | '24h' = '12h'): string {
  return formatClock(date.getHours(), date.getMinutes(), timeFormat);
}

/**
 * A stored instant as a display time in `timeZone`, not the browser's.
 *
 * `formatTime` reads LOCAL getters, which is correct everywhere the grid
 * renders in the viewer's own zone. It is wrong inside the event dialog, whose
 * form reads and writes in the PICKER's zone: the conflict warnings listed the
 * clashing events in browser-local time right beside a start and end the user
 * had just entered in another zone, so two events that genuinely overlap could
 * be shown hours apart. Invisible until a non-local zone is chosen, which is
 * why it went unnoticed.
 */
export function formatTimeInZone(
  iso: string,
  timeZone: string,
  timeFormat: '12h' | '24h' = '12h',
): string {
  const zone = usableTimeZone(timeZone);
  const instant = new Date(iso).getTime();
  if (!Number.isFinite(instant)) return '';
  // Shifted so the UTC getters read the wall clock in `zone` — the same trick
  // `utcIsoToZonedInputs` uses, and the reason this reads UTC getters where
  // `formatTime` reads local ones.
  const shifted = new Date(instant + timeZoneOffsetMs(instant, zone));
  return formatClock(shifted.getUTCHours(), shifted.getUTCMinutes(), timeFormat);
}

export function formatTimeRange(
  startAt: string,
  endAt: string,
  isAllDay: boolean,
  timeFormat: '12h' | '24h' = '12h',
  /**
   * The day this row is listed under. A multi-day booking is listed on every
   * day it covers (as Google's schedule view does), so without this every row
   * repeated the event's ORIGINAL start — a Mon 9am–Wed 5pm conference read
   * "9:00 AM – 5:00 PM" on Tuesday and Wednesday too, which is the same
   * mislabelling the month and week views were just fixed for.
   */
  onDay?: Date,
): string {
  if (isAllDay) return 'All day';
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (!onDay) {
    return `${formatTime(start, timeFormat)} – ${formatTime(end, timeFormat)}`;
  }
  const dayStart = startOfDay(onDay);
  const dayEnd = addDays(dayStart, 1);
  const startsEarlier = start < dayStart;
  const endsLater = end > dayEnd;
  if (startsEarlier && endsLater) return 'All day';
  if (startsEarlier) return `until ${formatTime(end, timeFormat)}`;
  if (endsLater) return `from ${formatTime(start, timeFormat)}`;
  return `${formatTime(start, timeFormat)} – ${formatTime(end, timeFormat)}`;
}

export function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Inverse of `formatDateKey` — local midnight on that calendar day. */
export function parseDateKey(key: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function formatAgendaDate(date: Date): string {
  const today = new Date();
  const tomorrow = addDays(today, 1);

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, tomorrow)) return 'Tomorrow';

  return `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}`;
}

// =============================================================================
// NAVIGATION
// =============================================================================

export function navigateDate(
  currentDate: Date,
  view: CalendarView,
  direction: 'prev' | 'next',
): Date {
  const delta = direction === 'next' ? 1 : -1;

  switch (view) {
    case 'month':
      return addMonths(currentDate, delta);
    case 'week':
      return addWeeks(currentDate, delta);
    case 'day':
      return addDays(currentDate, delta);
    case 'agenda':
      return addDays(currentDate, delta * 7);
  }
}

// =============================================================================
// VIDEO CALL DETECTION
// =============================================================================

export type VideoProvider = 'zoom' | 'meet' | 'teams' | 'webex';

export interface VideoCallInfo {
  provider: VideoProvider;
  url: string;
}

const VIDEO_PATTERNS: { provider: VideoProvider; regex: RegExp }[] = [
  { provider: 'zoom', regex: /https?:\/\/[\w.-]*zoom\.us\/j\/\S+/ },
  { provider: 'meet', regex: /https?:\/\/meet\.google\.com\/[\w-]+/ },
  { provider: 'teams', regex: /https?:\/\/teams\.microsoft\.com\/l\/meetup-join\/\S+/ },
  { provider: 'webex', regex: /https?:\/\/[\w.-]*webex\.com\/meet\/\S+/ },
];

export function detectVideoLink(event: {
  url?: string;
  location?: string;
  description?: string;
}): VideoCallInfo | null {
  const fields = [event.url, event.location, event.description];
  for (const field of fields) {
    if (!field) continue;
    for (const { provider, regex } of VIDEO_PATTERNS) {
      const match = field.match(regex);
      if (match) {
        return { provider, url: match[0] };
      }
    }
  }
  return null;
}

export const VIDEO_PROVIDER_LABELS: Record<VideoProvider, string> = {
  zoom: 'Join Zoom',
  meet: 'Join Meet',
  teams: 'Join Teams',
  webex: 'Join Webex',
};

// =============================================================================
// CONFLICT DETECTION
// =============================================================================

/**
 * Find events that overlap with a proposed time range.
 * Ignores all-day events and optionally excludes the event being edited.
 */
export function findConflicts(
  startAt: string,
  endAt: string,
  events: EventInstance[],
  excludeEventId?: string,
): EventInstance[] {
  const start = new Date(startAt);
  const end = new Date(endAt);

  return events.filter((e) => {
    if (e.isAllDay) return false;
    if (excludeEventId && e.eventId === excludeEventId) return false;
    const eStart = new Date(e.startAt);
    const eEnd = new Date(e.endAt);
    return eStart < end && eEnd > start;
  });
}

/**
 * Given a list of timed events for a single day, return the set of event IDs
 * that overlap with at least one other event.
 */
export function findOverlappingEventIds(events: EventInstance[]): Set<string> {
  const timed = events.filter((e) => !e.isAllDay);
  const overlapping = new Set<string>();

  for (let i = 0; i < timed.length; i++) {
    const aStart = new Date(timed[i].startAt);
    const aEnd = new Date(timed[i].endAt);
    for (let j = i + 1; j < timed.length; j++) {
      const bStart = new Date(timed[j].startAt);
      const bEnd = new Date(timed[j].endAt);
      if (aStart < bEnd && bStart < aEnd) {
        overlapping.add(timed[i].eventId);
        overlapping.add(timed[j].eventId);
      }
    }
  }

  return overlapping;
}

// =============================================================================
// BREAK GAP DETECTION
// =============================================================================

export interface BreakWarning {
  eventBefore: EventInstance;
  eventAfter: EventInstance;
  gapMinutes: number;
}

/**
 * Find pairs of adjacent (non-overlapping) events on a given day where the gap
 * between them is positive but less than `minBreakMinutes`.
 */
export function findInsufficientBreaks(
  events: EventInstance[],
  day: Date,
  minBreakMinutes: number,
): BreakWarning[] {
  if (minBreakMinutes <= 0) return [];

  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);

  const timed = events
    .filter((e) => {
      // Multi-day bars are context, not appointments: a three-day conference
      // would otherwise report an insufficient break against every meeting
      // inside it.
      if (isMultiDayBarEvent(e)) return false;
      const s = new Date(e.startAt);
      const end = new Date(e.endAt);
      return s < dayEnd && end > dayStart;
    })
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const warnings: BreakWarning[] = [];

  for (let i = 0; i < timed.length - 1; i++) {
    const endA = new Date(timed[i].endAt);
    const startB = new Date(timed[i + 1].startAt);
    const gapMs = startB.getTime() - endA.getTime();

    if (gapMs > 0 && gapMs < minBreakMinutes * 60 * 1000) {
      warnings.push({
        eventBefore: timed[i],
        eventAfter: timed[i + 1],
        gapMinutes: Math.round(gapMs / 60000),
      });
    }
  }

  return warnings;
}

// =============================================================================
// EVENT COLOR
// =============================================================================

export function getEventColor(
  event: EventInstance,
  colorPriority: 'event' | 'calendar' = 'event',
): string {
  if (colorPriority === 'calendar') {
    return event.calendarColor || event.color || '#3b82f6';
  }
  return event.color || event.calendarColor || '#3b82f6';
}

// =============================================================================
// WEATHER
// =============================================================================

/** Exact conversion; the rounding for display happens in `formatTemperature`. */
export function celsiusToFahrenheit(celsius: number): number {
  return celsius * (9 / 5) + 32;
}

/**
 * Render a Celsius reading in the user's unit. Forecast data is stored in
 * Celsius everywhere (see `WeatherDay`), so this is the only place the unit
 * choice is applied.
 */
export function formatTemperature(celsius: number, unit: TemperatureUnit = 'fahrenheit'): string {
  const value = unit === 'celsius' ? celsius : celsiusToFahrenheit(celsius);
  return `${Math.round(value)}°`;
}

// =============================================================================
// EVENT DRAFTS
// =============================================================================

/**
 * The time a click on a whole day (month view) should default an event to.
 *
 * Working-hours start is the predictable answer for any other day. For today
 * that hour is often already past, so fall back to the next snap boundary from
 * now — the same behaviour as reaching for the calendar mid-afternoon and
 * expecting the new event to land ahead of you rather than behind.
 */
export function defaultEventStartForDay(
  date: Date,
  workingHoursStart: number,
  snapIntervalMinutes: number = 15,
  now: Date = new Date(),
): Date {
  const start = startOfDay(date);
  start.setHours(workingHoursStart, 0, 0, 0);
  if (!isSameDay(date, now) || now <= start) return start;

  const snap = Math.max(1, snapIntervalMinutes);
  const next = new Date(now);
  next.setSeconds(0, 0);
  next.setMinutes(Math.ceil(next.getMinutes() / snap) * snap);
  // Rolling past midnight would move the event off the clicked day.
  return isSameDay(next, date) ? next : start;
}

/**
 * Build an all-day draft covering `from`..`to` inclusive.
 *
 * The end is the EXCLUSIVE UTC midnight after the last day — the same
 * convention EventDialog writes, so a dragged event and a hand-entered one
 * round-trip identically. (It was `23:59:59.999` local; the body below has
 * produced the exclusive-UTC-midnight form since the all-day date fix, and
 * this docstring described the encoding it replaced.)
 */
export function buildAllDayDraft(from: Date, to: Date): QuickEventDraft {
  const [first, last] = from <= to ? [from, to] : [to, from];
  // All-day is a DATE, so it is encoded the same way the event dialog encodes
  // it — UTC midnight with an exclusive UTC-midnight end. This used to use
  // local startOfDay/endOfDay, which put every user east of UTC one day early:
  // the stored instant fell on the previous UTC date, and the grid reads all-day
  // events by UTC date components. Fixing only the dialog left the two creation
  // paths disagreeing about what an all-day event is.
  const { startAt, endAt } = encodeAllDayRange(formatDateKey(first), formatDateKey(last));
  return { start: new Date(startAt), end: new Date(endAt), isAllDay: true };
}

/** Build a draft covering `durationMinutes` from `start`. */
export function buildTimedDraft(start: Date, durationMinutes: number): QuickEventDraft {
  return {
    start: new Date(start),
    end: new Date(start.getTime() + Math.max(1, durationMinutes) * 60000),
    isAllDay: false,
  };
}

/**
 * Whether a delete can be undone, and therefore whether the delete toast
 * offers an Undo.
 *
 *   - non-recurring / `all` -> soft-deletes the event row (`deleted_at`).
 *     Undone by `POST /events/:id/restore`, and the restore pushes cleanly to
 *     every provider because it is an ordinary event update.
 *   - `this` -> LOCALLY reversible (the server can clear the deletion
 *     exception, and `restoreVaultCalendarEventOccurrence` does exactly that)
 *     but DELIBERATELY not offered. A restored exception carries no override
 *     fields, so `overrideFieldGroups` derives an empty set and the engine
 *     pushes `{}` — Google leaves the occurrence `cancelled`, Microsoft 404s
 *     on a deleted instance, and the engine then records the empty result as
 *     the new sync base and never retries. The occurrence would come back in
 *     Fias and stay gone on the phone, permanently and silently. Apple works
 *     only incidentally, because its provider strips the EXDATE while
 *     upserting an override. Re-enable once there is a provider-neutral
 *     "resurrect occurrence" operation with end-to-end sync tests.
 *   - `this_and_future` -> overwrites the series rrule and HARD-deletes future
 *     exception rows. Nothing to restore from at all.
 *
 * A button that silently fails to restore is worse than no button.
 */
export function isDeleteReversible(
  event: { isRecurring?: boolean },
  scope: RecurrenceEditScope,
): boolean {
  return !event.isRecurring || scope === 'all';
}

/**
 * Whether an edit at this scope moves the series MASTER's own start.
 *
 * `all` does. So does `this_and_future`: it overwrites the series rrule (see
 * `isDeleteReversible` above). Only `this` leaves the master alone. No scope at
 * all means a non-recurring event, which is trivially its own master.
 *
 * Load-bearing, because a weekly master starting on a day its own BYDAY
 * excludes is invalid — Graph refuses the push, and locally the series expands
 * from a start no occurrence matches, so the moved occurrence vanishes. The
 * rule has to be brought along with the start.
 *
 * Extracted because the two callers disagreed: the dialog's save included
 * `this_and_future`, the grid's drag-move tested `scope === 'all'` only, so
 * dragging a "this and following" edit skipped the adjustment the dialog knew
 * to make. Same decision, two answers, in files that get edited together.
 */
export function scopeTouchesSeries(scope?: RecurrenceEditScope): boolean {
  return !scope || scope === 'all' || scope === 'this_and_future';
}

/**
 * The `instanceDate` a scoped (`this` / `this_and_future`) event mutation must
 * send — the occurrence's ORIGINAL start, i.e. the slot the recurrence rule
 * generated it in.
 *
 * `originalStartAt` is only populated on EXCEPTION instances — occurrences
 * that were already moved or overridden. For an ordinary expanded occurrence
 * it is undefined, and its original start is simply its `startAt`, because
 * nothing has shifted it. Sending undefined makes the server reject the whole
 * request with "instanceDate required for scope='this'", which is exactly
 * what deleting one occurrence of a series used to do.
 */
export function occurrenceInstanceDate(event: {
  originalStartAt?: string;
  startAt: string;
}): string {
  return event.originalStartAt ?? event.startAt;
}
